<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import AgentSection from './AgentSection.vue';
import AgentReadinessChecklist from './AgentReadinessChecklist.vue';

const props = defineProps({
  metrics: {
    type: Array,
    default: () => [],
  },
  checklist: {
    type: Array,
    default: () => [],
  },
  improvements: {
    type: Array,
    default: () => [],
  },
  canManageScenarios: { type: Boolean, default: false },
  canUseTools: { type: Boolean, default: false },
  canPublish: { type: Boolean, default: false },
  canTest: { type: Boolean, default: false },
});

const emit = defineEmits(['selectSection', 'openRoute']);
const { t } = useI18n();

const shortcuts = computed(() =>
  [
    {
      label: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_KNOWLEDGE'),
      helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_KNOWLEDGE_HELPER'),
      icon: 'i-lucide-library-big',
      section: 'knowledge',
    },
    {
      label: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_SCENARIOS'),
      helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_SCENARIOS_HELPER'),
      icon: 'i-lucide-route',
      route: 'captain_assistants_scenarios_index',
      available: props.canManageScenarios,
    },
    {
      label: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_TOOLS'),
      helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_TOOLS_HELPER'),
      icon: 'i-lucide-wrench',
      route: 'captain_account_tools_index',
      available: props.canUseTools,
    },
    {
      label: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_PUBLISH'),
      helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUT_PUBLISH_HELPER'),
      icon: 'i-lucide-rocket',
      section: 'publication',
      available: props.canPublish,
    },
  ].filter(shortcut => shortcut.available !== false)
);

const handleShortcut = shortcut => {
  if (shortcut.route) emit('openRoute', shortcut.route);
  else emit('selectSection', shortcut.section);
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <CardLayout v-for="metric in metrics" :key="metric.label">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-xs font-medium text-n-slate-10">
              {{ metric.label }}
            </p>
            <p class="mt-1 text-2xl font-semibold text-n-slate-12">
              {{ metric.value }}
            </p>
          </div>
          <span
            class="size-5 shrink-0 text-n-blue-11"
            :class="metric.icon"
            aria-hidden="true"
          />
        </div>
        <p v-if="metric.helper" class="text-xs leading-relaxed text-n-slate-10">
          {{ metric.helper }}
        </p>
      </CardLayout>
    </div>

    <div class="grid gap-6 xl:grid-cols-2">
      <AgentSection :title="t('CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_TITLE')">
        <AgentReadinessChecklist :items="checklist" />
      </AgentSection>

      <AgentSection
        :title="t('CAPTAIN_EXPERIENCE.AUTO.MELHORIAS_RECOMENDADAS')"
      >
        <div class="flex flex-col divide-y divide-n-weak">
          <article
            v-for="item in improvements.slice(0, 4)"
            :key="item.title"
            class="flex items-start gap-3 py-3 first:pt-0 last:pb-0"
          >
            <span
              class="mt-0.5 size-4 shrink-0 text-n-slate-10"
              :class="item.icon"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ item.title }}
              </p>
              <p class="mt-1 text-xs leading-relaxed text-n-slate-10">
                {{ item.helper }}
              </p>
            </div>
          </article>
        </div>
      </AgentSection>
    </div>

    <AgentSection :title="t('CAPTAIN_EXPERIENCE.WORKSPACE.SHORTCUTS_TITLE')">
      <div class="grid gap-3 sm:grid-cols-2">
        <button
          v-for="shortcut in shortcuts"
          :key="shortcut.label"
          type="button"
          class="group flex min-h-20 items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 text-left transition hover:border-n-brand hover:bg-n-alpha-2"
          @click="handleShortcut(shortcut)"
        >
          <span
            class="mt-0.5 size-5 shrink-0 text-n-blue-11"
            :class="shortcut.icon"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1">
            <span class="block text-sm font-medium text-n-slate-12">
              {{ shortcut.label }}
            </span>
            <span class="mt-1 block text-xs leading-relaxed text-n-slate-10">
              {{ shortcut.helper }}
            </span>
          </span>
          <span
            class="i-lucide-arrow-right size-4 shrink-0 text-n-slate-9 transition group-hover:translate-x-0.5 group-hover:text-n-blue-11"
            aria-hidden="true"
          />
        </button>
      </div>
      <Button
        v-if="canTest"
        class="mt-4"
        color="slate"
        variant="faded"
        icon="i-lucide-play"
        :label="t('CAPTAIN_EXPERIENCE.WORKSPACE.OPEN_CANONICAL_TEST')"
        @click="emit('openRoute', 'captain_assistants_playground_index')"
      />
    </AgentSection>
  </div>
</template>
